import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { PasswordInput } from '../components/common';
import { isPortalInstitutie } from '../utils';

const css = `
  * { box-sizing: border-box; }
  .login-page { min-height: 100vh; display: flex; align-items: center; justify-content: center; background: #f5f5f7; padding: 20px; }
  .login-card { background: #fff; border-radius: 16px; padding: 40px; width: 100%; max-width: 420px; box-shadow: 0 4px 24px rgba(0,0,0,0.08); }
  .login-inp { padding: 12px 14px; font-size: 16px; border: 1.5px solid #e5e7eb; border-radius: 10px; outline: none; font-family: inherit; width: 100%; -webkit-appearance: none; }
  .login-inp:focus { border-color: #2d2d7e; }
  .login-btn { padding: 14px; font-size: 16px; font-weight: 600; background: #2d2d7e; color: #fff; border: none; border-radius: 10px; cursor: pointer; width: 100%; -webkit-appearance: none; }
  .login-btn:disabled { opacity: 0.6; cursor: not-allowed; }
  .login-err { background: #fee2e2; color: #dc2626; padding: 10px 14px; border-radius: 8px; font-size: 13px; }
  .reg-cta { display: flex; align-items: center; gap: 10px; border: 1.5px solid #e5e7eb; border-radius: 10px; padding: 12px 14px; cursor: pointer; text-decoration: none; color: #1a1a2e; transition: all .15s; }
  .reg-cta:hover { border-color: #2d2d7e; background: #f8f8ff; }
  .eudi-btn { display: flex; align-items: center; gap: 10px; width: 100%; padding: 13px 16px; background: linear-gradient(135deg, #003399 0%, #0052CC 100%); color: #fff; border: none; border-radius: 10px; cursor: pointer; font-size: 15px; font-weight: 600; font-family: inherit; transition: opacity .15s; }
  .eudi-btn:hover { opacity: .9; }
  .eudi-btn:disabled { opacity: .5; cursor: not-allowed; }
  .eudi-qr-box { text-align: center; padding: 20px 0 8px; }
  .eudi-qr-box img { border-radius: 12px; border: 2px solid #003399; }
  .eudi-status { font-size: 13px; color: #6b7280; margin-top: 10px; }
  .eudi-ok { color: #059669; font-weight: 600; }
  @media (max-width: 599px) {
    .login-page { padding: 0; align-items: flex-start; }
    .login-card { border-radius: 0; padding: 32px 16px; min-height: 100vh; box-shadow: none; }
    .login-btn { padding: 16px; }
  }
`;

export default function LoginPage() {
  const portal           = isPortalInstitutie();
  const [numePrimarie, setNumePrimarie] = useState('');
  const navigate         = useNavigate();
  const [searchParams]   = useSearchParams();
  const confirmat        = searchParams.get('confirmat') === '1';
  const { login, verifyOTP, loginWithEudi } = useAuth();
  const [email, setEmail]     = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp]         = useState('');
  const [userId, setUserId]   = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [step, setStep]       = useState('login'); // 'login' | 'otp'

  // ── EUDI Wallet state ──────────────────────────────────────
  const [eudiStep, setEudiStep]   = useState(null); // null | 'loading' | 'qr' | 'waiting' | 'done' | 'error'
  const [eudiQR, setEudiQR]       = useState(null);
  const [eudiSession, setEudiSession] = useState(null);
  const [eudiMsg, setEudiMsg]     = useState('');
  const pollRef = useRef(null);

  useEffect(() => () => clearInterval(pollRef.current), []);

  useEffect(() => {
    api.get('/util/info-utile').then(r => {
      const nume = r.data?.data?.primarie?.nume;
      if (nume) { setNumePrimarie(nume); document.title = nume; }
    }).catch(() => {});
  }, []);

  async function startEudi() {
    setEudiStep('loading');
    setError('');
    try {
      const { data } = await api.post('/eudi/start');
      setEudiQR(data.qrCode);
      setEudiSession(data.sessionId);
      setEudiStep('qr');
      setEudiMsg('Scanați QR code-ul cu aplicația EUDI Wallet');
      // Polling la fiecare 2 secunde
      pollRef.current = setInterval(() => pollEudi(data.sessionId), 2000);
    } catch {
      setEudiStep('error');
      setEudiMsg('Nu am putut iniția sesiunea. Reîncercați.');
    }
  }

  async function pollEudi(sessionId) {
    try {
      const { data } = await api.get(`/eudi/status/${sessionId}`);
      if (data.status === 'completed') {
        clearInterval(pollRef.current);
        setEudiStep('done');
        setEudiMsg(`Bun venit, ${data.user.prenume} ${data.user.nume}!`);
        loginWithEudi(data);
        setTimeout(() => navigate('/dashboard'), 1200);
      } else if (data.status === 'failed' || data.status === 'expired') {
        clearInterval(pollRef.current);
        setEudiStep('error');
        setEudiMsg('Autentificare eșuată sau expirată. Reîncercați.');
      }
    } catch { /* continuăm polling-ul */ }
  }

  function cancelEudi() {
    clearInterval(pollRef.current);
    setEudiStep(null);
    setEudiQR(null);
    setEudiSession(null);
  }

  async function handleLogin(e) {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const result = await login(email, password);
      if (result.requiresOTP) {
        setUserId(result.userId);
        setStep('otp');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Email sau parolă incorectă');
    } finally {
      setLoading(false);
    }
  }

  async function handleOTP(e) {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await verifyOTP(userId, otp);
      navigate('/dashboard');
    } catch {
      setError('Cod OTP invalid sau expirat');
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <style>{css}</style>
      <div className="login-page">
        <div className="login-card">
          <div style={{ fontSize: '36px', textAlign: 'center', marginBottom: '12px' }}>{portal ? '🏢' : '🏛'}</div>
          <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#1a1a2e', textAlign: 'center', margin: '0 0 6px' }}>
            {numePrimarie || 'CivicApp'}{portal ? ' — Portal Instituție' : ''}
          </h1>
          <p style={{ fontSize: '14px', color: '#6b7280', textAlign: 'center', margin: '0 0 28px' }}>
            {step === 'otp' ? 'Introduceți codul de verificare' : portal ? 'Autentificare personal instituție' : 'Autentificați-vă în cont'}
          </p>

          {confirmat && (
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '12px 14px', marginBottom: '16px', fontSize: '14px', color: '#166534', textAlign: 'center' }}>
              Contul a fost activat cu succes! Te poți autentifica acum.
            </div>
          )}
          {error && <div className="login-err" style={{ marginBottom: '16px' }}>{error}</div>}

          {step === 'login' ? (
            <>
              <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={{ fontSize: '13px', fontWeight: '500', color: '#374151' }}>Email</label>
                  <input type="email" className="login-inp" value={email} onChange={e => setEmail(e.target.value)}
                    placeholder="email@exemplu.ro" autoComplete="email" required />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={{ fontSize: '13px', fontWeight: '500', color: '#374151' }}>Parolă</label>
                  <PasswordInput className="login-inp" value={password} onChange={e => setPassword(e.target.value)}
                    placeholder="••••••••" autoComplete="current-password" required />
                </div>
                <div style={{ textAlign: 'right', marginTop: '-4px' }}>
                  <Link to="/forgot-password" style={{ fontSize: '13px', color: '#6b7280', textDecoration: 'none' }}>Am uitat parola</Link>
                </div>
                <button type="submit" disabled={loading} className="login-btn">
                  {loading ? 'Se autentifică...' : 'Autentificare'}
                </button>
              </form>

              {/* ── EUDI Wallet (doar pe portalul cetățenilor — e identitate digitală de
                   cetățean, nu de personal instituție) ── */}
              {!portal && <div style={{ margin: '20px 0 0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px', color: '#9ca3af', fontSize: '13px' }}>
                  <div style={{ flex: 1, height: '1px', background: '#e5e7eb' }} />
                  sau
                  <div style={{ flex: 1, height: '1px', background: '#e5e7eb' }} />
                </div>

                {!eudiStep && (
                  <button className="eudi-btn" onClick={startEudi}>
                    <span style={{ fontSize: '22px' }}>🪪</span>
                    <div style={{ flex: 1, textAlign: 'left' }}>
                      <div>Portofelul digital european</div>
                      <div style={{ fontSize: '11px', fontWeight: '400', opacity: .85 }}>EUDI Wallet · eIDAS 2.0</div>
                    </div>
                    <span style={{ fontSize: '18px', opacity: .7 }}>›</span>
                  </button>
                )}

                {eudiStep === 'loading' && (
                  <div style={{ textAlign: 'center', padding: '16px', color: '#6b7280', fontSize: '14px' }}>
                    Se generează QR code...
                  </div>
                )}

                {eudiStep === 'qr' && eudiQR && (
                  <div className="eudi-qr-box">
                    <img src={eudiQR} alt="QR EUDI Wallet" width={280} height={280} />
                    <p className="eudi-status">{eudiMsg}</p>
                    <p style={{ fontSize: '12px', color: '#9ca3af', margin: '4px 0 12px' }}>
                      Pe mobil? <a href="#" onClick={e => { e.preventDefault(); setEudiStep('waiting'); }} style={{ color: '#003399' }}>Deschide wallet-ul</a>
                    </p>
                    <button onClick={cancelEudi} style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: '13px' }}>✕ Anulează</button>
                  </div>
                )}

                {eudiStep === 'waiting' && (
                  <div style={{ textAlign: 'center', padding: '16px' }}>
                    <div style={{ fontSize: '32px', marginBottom: '8px' }}>⏳</div>
                    <p className="eudi-status">Așteptăm confirmarea din wallet...</p>
                    <button onClick={cancelEudi} style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: '13px', marginTop: '8px' }}>✕ Anulează</button>
                  </div>
                )}

                {eudiStep === 'done' && (
                  <div style={{ textAlign: 'center', padding: '16px' }}>
                    <div style={{ fontSize: '32px', marginBottom: '8px' }}>✅</div>
                    <p className="eudi-ok">{eudiMsg}</p>
                  </div>
                )}

                {eudiStep === 'error' && (
                  <div style={{ textAlign: 'center', padding: '12px' }}>
                    <p style={{ color: '#dc2626', fontSize: '14px', margin: '0 0 8px' }}>{eudiMsg}</p>
                    <button className="eudi-btn" onClick={startEudi} style={{ justifyContent: 'center' }}>Reîncearcă</button>
                  </div>
                )}
              </div>}

              {/* Auto-înregistrare cetățean — ascunsă pe portalul de instituție, unde
                  conturile (functionar/admin/primar) sunt create doar de un admin. */}
              {!portal && <>
                {/* Separator */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '20px 0 16px', color: '#9ca3af', fontSize: '13px' }}>
                  <div style={{ flex: 1, height: '1px', background: '#e5e7eb' }} />
                  Nu ai cont? Înregistrează-te
                  <div style={{ flex: 1, height: '1px', background: '#e5e7eb' }} />
                </div>

                {/* CTA-uri înregistrare separate */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <Link to="/register?tip=fizica" className="reg-cta">
                    <span style={{ fontSize: '24px' }}>👤</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '600', fontSize: '14px' }}>Persoană fizică</div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>Cetățean individual cu CNP și CI</div>
                    </div>
                    <span style={{ color: '#9ca3af', fontSize: '16px' }}>›</span>
                  </Link>
                  <Link to="/register?tip=juridica" className="reg-cta">
                    <span style={{ fontSize: '24px' }}>🏢</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '600', fontSize: '14px' }}>Persoană juridică</div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>Firmă, ONG sau instituție cu CUI</div>
                    </div>
                    <span style={{ color: '#9ca3af', fontSize: '16px' }}>›</span>
                  </Link>
                </div>
              </>}
            </>
          ) : (
            <form onSubmit={handleOTP} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <p style={{ fontSize: '14px', color: '#555', textAlign: 'center', margin: 0 }}>
                Verificați emailul pentru codul de 6 cifre
              </p>
              <input type="text" inputMode="numeric" maxLength={6} className="login-inp"
                value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, ''))}
                placeholder="000000" autoComplete="one-time-code"
                style={{ textAlign: 'center', letterSpacing: '8px', fontSize: '24px', fontFamily: 'monospace' }} />
              <button type="submit" disabled={loading || otp.length !== 6} className="login-btn">
                {loading ? 'Se verifică...' : 'Verifică codul'}
              </button>
              <button type="button" onClick={() => { setStep('login'); setOtp(''); setError(''); }}
                style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: '14px', padding: '8px' }}>
                ← Înapoi la login
              </button>
            </form>
          )}
        </div>
      </div>
    </>
  );
}
